import { useEffect, useRef, useState } from 'react';
import { ArrowDownToLine, ArrowRight, CheckCheck, FileDiff, RefreshCw } from 'lucide-react';
import type { SourceRevision, State } from '../shared/model';
import {
  exportKindLabels,
  exportKinds,
  type ExportCandidate,
  type ExportWorkspaceData,
} from '../shared/export-changes';
import './export-changes.css';

const stamp = (value: string) =>
  new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
type View = 'disponiveis' | 'pendentes' | 'incorporadas';
export function ExportChangesWorkspace({
  data,
  refresh,
}: {
  data: State;
  refresh: () => Promise<unknown>;
}) {
  const [workspace, setWorkspace] = useState<ExportWorkspaceData>();
  const [view, setView] = useState<View>('disponiveis');
  const [kind, setKind] = useState('');
  const [selected, setSelected] = useState('');
  const [limit, setLimit] = useState(8);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [message, setMessage] = useState('');
  const [sources, setSources] = useState<{ base: string; current: string }>();
  const detail = useRef<HTMLElement>(null);
  async function request<T>(url: string, body?: unknown): Promise<T> {
    const response = await fetch(
      url,
      body === undefined
        ? undefined
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': data.csrf },
            body: JSON.stringify(body),
          },
    );
    const value = await response.json();
    if (!response.ok) throw new Error(value.error ?? 'Não foi possível concluir a operação.');
    return value as T;
  }
  async function load() {
    const value = await request<ExportWorkspaceData>('/api/export-changes');
    setWorkspace(value);
    return value;
  }
  useEffect(() => {
    let active = true;
    request<ExportWorkspaceData>('/api/export-changes')
      .then((value) => {
        if (active) setWorkspace(value);
      })
      .catch((cause) => {
        if (active) setError(cause.message);
      });
    return () => {
      active = false;
    };
  }, [data.sync?.id]);
  useEffect(() => {
    setSources(undefined);
    if (selected) detail.current?.focus({ preventScroll: true });
  }, [selected]);
  const available = workspace?.candidates.filter((row) => !row.existingChangeId) ?? [];
  const pending = workspace?.changes.filter((row) => row.status === 'pendente') ?? [];
  const incorporated = workspace?.changes.filter((row) => row.status === 'incorporada') ?? [];
  const rows = (
    view === 'disponiveis' ? available : view === 'pendentes' ? pending : incorporated
  ).filter((row) => !kind || row.entityKind === kind);
  const change = workspace?.changes.find((row) => row.id === selected);
  async function prepare(candidate: ExportCandidate) {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const value = await request<{ id: string }>('/api/export-changes', {
        entityKind: candidate.entityKind,
        entityId: candidate.entityId,
        entityVersion: candidate.entityVersion,
        previewHash: candidate.previewHash,
      });
      await load();
      setView('pendentes');
      setSelected(value.id);
      setMessage(
        'Revisão preparada. Confira o texto e a comparação antes de incorporar ao acervo.',
      );
    } catch (cause) {
      setError((cause as Error).message);
      await load().catch(() => {});
    } finally {
      setBusy(false);
    }
  }
  async function reimport() {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await request<{ message: string }>('/api/import', {});
      await load();
      await refresh();
      setMessage(result.message);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function compareSources() {
    if (!change) return;
    setBusy(true);
    setError('');
    try {
      const read = async (source: typeof change.baseSource) =>
        source
          ? (
              await request<SourceRevision>(
                `/api/source?${new URLSearchParams({ path: source.path, hash: source.hash })}`,
              )
            ).text
          : '(Arquivo não encontrado nesta revisão.)';
      const [base, current] = await Promise.all([
        read(change.baseSource),
        read(change.latestCheck?.observedSource ?? null),
      ]);
      setSources({ base, current });
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel export-workspace" aria-labelledby="export-title">
      <div className="export-heading">
        <div>
          <span className="eyebrow">Acervo FLG</span>
          <h2 id="export-title">Alterações para o acervo</h2>
          <p>
            Prepare uma revisão, confira o texto e incorpore ao arquivo indicado. A confirmação
            acontece na reimportação.
          </p>
        </div>
        <FileDiff size={25} aria-hidden="true" />
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {message && (
        <p className="export-notice" role="status">
          {message}
        </p>
      )}
      <div className="export-toolbar">
        <div className="export-tabs" aria-label="Estado das alterações">
          {(
            [
              ['disponiveis', 'Disponíveis', available.length],
              ['pendentes', 'Preparadas', pending.length],
              ['incorporadas', 'Incorporadas', incorporated.length],
            ] as const
          ).map(([value, label, count]) => (
            <button
              key={value}
              type="button"
              aria-pressed={view === value}
              onClick={() => {
                setView(value);
                setLimit(8);
              }}
            >
              {label} <span>{count}</span>
            </button>
          ))}
        </div>
        <label className="export-kind">
          Tipo de registro
          <select
            value={kind}
            onChange={(event) => {
              setKind(event.target.value);
              setLimit(8);
            }}
          >
            <option value="">Todos os tipos</option>
            {exportKinds.map((value) => (
              <option key={value} value={value}>
                {exportKindLabels[value]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {!workspace ? (
        <p>Carregando registros…</p>
      ) : (
        <>
          <p className="fine-print">
            {view === 'disponiveis'
              ? 'Registros operacionais sem uma revisão preparada para esta versão. Preparar não aprova nem altera a fonte.'
              : view === 'pendentes'
                ? 'O download mantém a pendência. Texto divergente ou arquivo ausente exigem conferência.'
                : 'Confirmações preservadas com o arquivo, hash e importação que localizaram o registro.'}
          </p>
          <div className="export-list">
            {rows.slice(0, limit).map((row) => (
              <article
                className="export-row"
                key={'id' in row ? row.id : `${row.entityKind}:${row.entityId}`}
              >
                <div>
                  <span className="eyebrow">{exportKindLabels[row.entityKind]}</span>
                  <h3>{row.title}</h3>
                  <p className="export-path">{row.targetPath}</p>
                  {'status' in row && (
                    <p className="fine-print">
                      {row.status === 'incorporada'
                        ? 'Registro encontrado no acervo'
                        : (row.latestCheck?.message ??
                          'Aguardando aplicação humana e reimportação.')}
                      {row.entityChanged && ' O registro operacional tem uma versão mais recente.'}
                    </p>
                  )}
                </div>
                {'id' in row ? (
                  <button onClick={() => setSelected(row.id)} aria-label={`Revisar ${row.title}`}>
                    Revisar <ArrowRight size={15} />
                  </button>
                ) : data.permissions.edit ? (
                  <button disabled={busy} onClick={() => prepare(row)}>
                    Preparar revisão
                  </button>
                ) : (
                  <span className="fine-print">Editor prepara a revisão</span>
                )}
              </article>
            ))}
          </div>
          {!rows.length && (
            <p className="export-empty">
              {view === 'disponiveis'
                ? 'Nenhum registro disponível neste filtro.'
                : view === 'pendentes'
                  ? 'Nenhuma revisão aguardando incorporação neste filtro.'
                  : 'Nenhuma incorporação confirmada neste filtro.'}
            </p>
          )}
          {rows.length > limit && (
            <button onClick={() => setLimit(limit + 8)}>
              Mostrar mais {Math.min(8, rows.length - limit)} registros
            </button>
          )}
        </>
      )}
      {change && (
        <section
          className="export-detail"
          tabIndex={-1}
          ref={detail}
          aria-labelledby="export-detail-title"
        >
          <div className="export-heading">
            <div>
              <span className="eyebrow">Revisão preservada</span>
              <h3 id="export-detail-title">{change.title}</h3>
            </div>
            <button onClick={() => setSelected('')}>Fechar revisão</button>
          </div>
          <p className="export-status">
            {change.status === 'incorporada' ? (
              <>
                <CheckCheck size={16} /> Incorporada ao acervo em{' '}
                {stamp(change.incorporation!.checkedAt)}
              </>
            ) : (
              'Registro no acervo pendente'
            )}
          </p>
          <dl className="export-facts">
            <div>
              <dt>Arquivo de destino</dt>
              <dd>{change.targetPath}</dd>
            </div>
            <div>
              <dt>Preparado por</dt>
              <dd>
                {change.preparedBy} · {stamp(change.createdAt)}
              </dd>
            </div>
            <div>
              <dt>Hash da revisão-base</dt>
              <dd>
                <code>{change.baseSource?.hash ?? 'Novo registro, sem fonte importada'}</code>
              </dd>
            </div>
            <div>
              <dt>Commit-base</dt>
              <dd>
                <code>{change.baseSource?.commit ?? 'Não informado'}</code>
              </dd>
            </div>
          </dl>
          {change.entityChanged && (
            <p className="export-warning">
              O registro operacional mudou depois desta preparação. Esta revisão preserva os dados
              anteriores; prepare a versão atual na aba Disponíveis.
            </p>
          )}
          {change.latestCheck && (
            <p
              className={
                ['base_alterada', 'texto_divergente', 'calendario_divergente'].includes(
                  change.latestCheck.result,
                )
                  ? 'export-warning'
                  : 'fine-print'
              }
            >
              {change.latestCheck.message}
            </p>
          )}
          <ul className="export-instructions">
            {change.instructions.map((instruction) => (
              <li key={instruction}>{instruction}</li>
            ))}
            <li>
              Após aplicar o texto, atualize o manifesto e valide o acervo. A reimportação verifica
              o registro completo no caminho esperado.
            </li>
          </ul>
          <details open>
            <summary>Texto para revisão e incorporação</summary>
            <pre aria-label="Texto da alteração">{change.markdown}</pre>
          </details>
          <details>
            <summary>Comparação com a revisão-base</summary>
            <p className="fine-print">
              Comparação de anexação para leitura; não é um patch executável. Preserve as versões
              existentes.
            </p>
            <pre aria-label="Diff da alteração">{change.diff}</pre>
          </details>
          <details>
            <summary>Entidades e evidências</summary>
            <ul>
              {change.affectedEntities.map((entity, index) => (
                <li key={`${entity.kind}:${entity.id}:${index}`}>
                  {entity.label} · <code>{entity.id}</code>
                </li>
              ))}
            </ul>
            {change.evidence.map((evidence, index) => (
              <p key={index}>{evidence}</p>
            ))}
          </details>
          {change.latestCheck && (
            <div className="export-source-comparison">
              <button disabled={busy} onClick={compareSources}>
                Comparar fonte-base e fonte reimportada
              </button>
              {sources && (
                <div className="export-source-columns">
                  <div>
                    <h4>Fonte-base preservada</h4>
                    <pre>{sources.base}</pre>
                  </div>
                  <div>
                    <h4>Fonte da última conferência</h4>
                    <pre>{sources.current}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
          {change.incorporation && (
            <p className="fine-print export-path">
              Confirmação: {change.incorporation.source.path} · SHA-256{' '}
              {change.incorporation.source.hash} · importação {change.incorporation.syncId}
            </p>
          )}
          <div className="export-actions">
            <a
              className="button"
              href={`/api/export-changes/${encodeURIComponent(change.id)}/download`}
              download
            >
              <ArrowDownToLine size={16} /> Baixar revisão em Markdown
            </a>
            <a
              href={`/api/export-changes/${encodeURIComponent(change.id)}/download?format=json`}
              download
            >
              Baixar dados da revisão
            </a>
            {data.permissions.admin && change.status === 'pendente' && (
              <button disabled={busy} onClick={reimport}>
                <RefreshCw size={15} /> Reimportar e conferir
              </button>
            )}
          </div>
        </section>
      )}
      <details className="export-technical">
        <summary>Snapshot operacional completo</summary>
        <p>
          O JSON completo inclui outros registros operacionais. Seu download não cria nem confirma
          alterações no acervo.
        </p>
        <a href="/api/export" download="flg-alteracoes.json">
          <ArrowDownToLine size={15} /> Baixar snapshot do workspace
        </a>
      </details>
    </section>
  );
}
